/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Drawer, Tooltip} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import AppVersion from '@ion/applications/src/components/AppVersion';
import {editAdminUser} from '@ion/applications/src/pages/admin/users/state';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import LogoutButton from '@ion/dls/src/components/buttons/LogoutButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';
import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {preInitializedSelector} from '@/store/selectors/admin-users';
import {isLoggingOutSelector} from '@/store/selectors/login';
import {useLogout} from '../hooks/useLogout';
import {formatUsername, hasLengthyChars, MAX_CHARACTERS} from '../utils/header-utils';

export interface DesktopUserProfileViewType {
  handleClick: Function;
  open: boolean;
  id: string;
  anchorEl: any;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  popup : {
    border          : `1px solid ${dlsColor('grey5')}`,
    boxSizing       : 'border-box',
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    backgroundColor : dlsColor('white')
  },
  ionLink : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLinkAlt')
  },
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    fontWeight : 500
  },
  userHover : {
    color  : ({enableProfile}: {enableProfile: boolean}) => enableProfile ? dlsColor('grey1') : dlsColor('grey5'),
    cursor : ({enableProfile}: {enableProfile: boolean}) => enableProfile ?'pointer' : 'default'
  },
  options : {
    fontSize   : '14px',
    lineHeight : '20px',
    color      : dlsColor('grey2'),
    fontWeight : 400,
    cursor     : 'pointer'
  },
  disabled : {
    color         : dlsColor('grey4'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    ...dlsTypography('body3')
  },
  logOutWrapper : {
    display : 'flex'
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  },
  container : {
    width           : 320,
    backgroundColor : dlsColor('white')
  },
  userWrapper : {
    width        : '100%',
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  margin : {
    marginLeft  : 20,
    marginRight : 20
  },
  subContainer : {
    width      : '100%',
    alignItems : 'center',
    display    : 'flex',
    height     : 84
  },
  optionWrapper : {
    width          : '100%',
    height         : 48,
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  previewWrapper : {
    display        : 'flex',
    width          : '100%',
    justifyContent : 'space-between'
  },
  switch : {
    display    : 'flex',
    alignItems : 'center'
  },
  drawerRoot : {
    marginLeft : '-20px',
    zIndex     : `${dlsZIndex('desktopUserProfileViewPopover')} !important` as any
  }
}));

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip')
  }
}));

function IconCustomTooltip(props) {
  return <Tooltip arrow classes={iconsTooltip()} {...props} />;
}

/**
 * Launches a separate tab to display the CalAmp privacy policy page.
 *
 * ```typescript
 *   import {viewPrivacyPolicy} from '@ion/ion-suite/src/components/header/DesktopProfileView';
 *
 *   viewPrivacyPolicy(closeMenuFunc);
 *
 * ```
 *
 * @param  postOpenFunc An optional function to execute following the launch of the
 *         privacy policy page (ex. function to close the menu from which the launch
 *         was initiated).
 */
export const viewPrivacyPolicy = function(postOpenFunc?: Function) {
  return function() {
    window.open(get('privacyPolicyURL'), '_blank');
    postOpenFunc?.();
  };
};

/**
 * Launches a separate tab to display the CalAmp API Reference page.
 *
 * ```typescript
 *   import {viewApiReference} from '@ion/ion-suite/src/components/header/DesktopProfileView';
 *
 *   viewApiReference(closeMenuFunc);
 *
 * ```
 *
 * @param  postOpenFunc An optional function to execute following the launch of the
 *         privacy policy page (ex. function to close the menu from which the launch
 *         was initiated).
 */
export const launchURL = function(entry: string, postOpenFunc?: Function) {
  return function() {
    const url = get(entry).concat('/');
    window.open(url, '_blank');
    postOpenFunc?.();
  };
};

/**
 * Launches a separate tab to display the CalAmp Help page.
 *
 * ```typescript
 *   import {viewHelp} from '@ion/ion-suite/src/components/header/DesktopProfileView';
 *
 *   viewHelp(closeMenuFunc);
 *
 * ```
 * @param  postOpenFunc An optional function to execute following the launch of the
 *         Help page (ex. function to close the menu from which the launch
 *         was initiated).
 */
export const viewHelp = function(postOpenFunc?: Function) {
  return function() {
    window.open(get('helpDocumentURL'), '_blank');
    postOpenFunc?.();
  };
};

/**
 * @DesktopUserProfileView
 *
 * The Desktop user profile view  component provides the application user profile view
 * logout option and
 * ion preview.
 */
export const DesktopUserProfileView = ({
  handleClick,
  open,
  id,
  anchorEl
}: DesktopUserProfileViewType) => {
  const dispatch      = useDispatch();
  const isLoggingOut  = useSelector(isLoggingOutSelector);
  const enableProfile = useSelector(preInitializedSelector);

  const {currentUser} = useCurrentUser();
  const {username}    = currentUser;
  const accountName   = currentUser?.account?.title;

  const onLogout      = useLogout();
  const classes       = useStyles({enableProfile: true});

  const truncateUserName = hasLengthyChars(username, MAX_CHARACTERS);

  return (
    <>
      <Drawer anchor={'right'} id={id}
        classes={{root: classes.drawerRoot}}
        open={open}
        onClose={function(e) { handleClick(e); }}>
        <div className={classes.container}>
          <div className={classes.userWrapper}>
            <div className={classes.margin}>
              <div className={classes.subContainer}>
                <div>
                  <Avatar name={username} />
                </div>
                <div style={{marginLeft: '24px'}}>
                  <label className={classes.user}>
                    <ConditionalRender condition={truncateUserName}>
                      <IconCustomTooltip title={<span>{username}</span>}>
                        <span>{formatUsername(username, MAX_CHARACTERS)}</span>
                      </IconCustomTooltip>
                    </ConditionalRender>
                    <ConditionalRender condition={!truncateUserName}>
                      <span>{username}</span>
                    </ConditionalRender>
                  </label>
                  <label className={classes.account}>{accountName}</label>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.margin}>
            <div className={classes.optionWrapper}>
              <div>
                <CTALink className={classnames(classes.user, classes.userHover)} onClick={enableProfile ? function() {
                  handleClick();
                  dispatch(editAdminUser(currentUser, true, false));
                } : undefined}>
                  {translate('nav:header.profile')}
                </CTALink>
              </div>
            </div>
            <div className={classes.optionWrapper}>
              <div className={classes.margin}>
                <CTALink className={classes.options}
                  onClick={launchURL('knowledgeBaseURL', handleClick)}>
                  {translate('nav:header.knowledgeBase')}
                </CTALink>
              </div>
            </div>
            <div className={classes.optionWrapper}>
              <div className={classes.margin}>
                <CTALink className={classes.options}
                  onClick={launchURL('developerPortalURL', handleClick)}>
                  {translate('nav:header.developerPortal')}
                </CTALink>
              </div>
            </div>
            <div className={classes.optionWrapper}>
              <div className={classes.margin}>
                <CTALink className={classes.options}
                  onClick={viewPrivacyPolicy(handleClick)}>
                  {translate('nav:header.privacyPolicy')}
                </CTALink>
              </div>
            </div>
            <div className={classes.optionWrapper}>
              <AppVersion condensed includeVersionText />
            </div>
            <div className={classes.logOutWrapper}>
              <LogoutButton label={translate('nav:header.logout')}
                onLogout={onLogout}
                isLoggingOut={isLoggingOut}
                iconOnly={false}/>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DesktopUserProfileView;
